import React, { useState, useEffect } from 'react';

export default function PreferencesStep({ onNext, onBack, data }) {
  const [notifications, setNotifications] = useState({
    priceAlerts: data.notifications?.priceAlerts ?? true,
    weeklyReports: data.notifications?.weeklyReports ?? true,
    indicatorChanges: data.notifications?.indicatorChanges ?? false
  });
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);

  const notificationOptions = [
    {
      id: 'priceAlerts',
      title: 'Price alerts',
      description: 'Get notified when your coins hit target prices',
      color: '#f59e0b', // Orange
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/>
        </svg>
      )
    },
    {
      id: 'weeklyReports',
      title: 'Weekly AI reports',
      description: 'Receive weekly insights and market analysis',
      color: '#10b981', // Green
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
      )
    },
    {
      id: 'indicatorChanges',
      title: 'Indicator changes',
      description: 'Alerts when technical indicators change significantly',
      color: '#8b5cf6', // Purple
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z"/>
        </svg>
      )
    }
  ];

  // Check notification permission on component mount
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      alert('This browser does not support notifications');
      return;
    }

    setIsRequestingPermission(true);
    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      
      if (permission === 'granted') {
        // Send a test notification
        new Notification('CryptoIndicators.AI', {
          body: 'Notifications enabled! You\'ll now receive price alerts and updates.',
          icon: '/logo192.png',
          badge: '/logo192.png'
        });
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    } finally {
      setIsRequestingPermission(false);
    }
  };

  const toggleNotification = async (key) => {
    const newValue = !notifications[key];
    
    // If enabling notifications and permission not granted, request it
    if (newValue && notificationPermission !== 'granted') {
      await requestNotificationPermission();
      // Only proceed if permission was granted
      if (Notification.permission !== 'granted') {
        return;
      }
    }

    setNotifications(prev => ({
      ...prev,
      [key]: newValue
    }));

    // Send a test notification if enabling
    if (newValue && Notification.permission === 'granted') {
      const option = notificationOptions.find(opt => opt.id === key);
      new Notification('CryptoIndicators.AI', {
        body: `${option.title} enabled! You'll now receive ${option.title.toLowerCase()}.`,
        icon: '/logo192.png',
        badge: '/logo192.png'
      });
    }
  };

  const handleNext = () => {
    onNext({ notifications });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col">
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cdefs%3E%3Cpattern id='grid' width='10' height='10' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 10 0 L 0 0 0 10' fill='none' stroke='rgba(255,255,255,0.1)' stroke-width='0.5'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100' height='100' fill='url(%23grid)'/%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="flex-1 flex flex-col justify-start items-center px-4 pt-4 relative z-10">
        {/* Logo and Brand */}
        <div className="text-center mb-6 px-2 mt-2">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-2 shadow-lg">
            <span className="text-white font-bold text-lg">CI</span>
          </div>
          <h1 className="text-white text-lg font-semibold">CryptoIndicators.AI</h1>
        </div>

        {/* Main Question */}
        <div className="text-center mb-6 max-w-md px-2">
          <h2 className="text-2xl font-bold text-white mb-2">
            Want updates?
          </h2>
          <p className="text-gray-400 text-base">
            Let's personalize your notification experience.
          </p>
        </div>

        {/* Notification Options */}
        <div className="w-full max-w-md space-y-3 mb-6 px-2">
          {notificationOptions.map((option) => (
            <div
              key={option.id}
              className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 flex items-center justify-between"
            >
              <div className="flex items-center space-x-4">
                {/* Colored Icon Circle */}
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white flex-shrink-0"
                  style={{ backgroundColor: option.color }}
                >
                  {option.icon}
                </div>
                
                {/* Text Content */}
                <div className="flex-1">
                  <h3 className="font-semibold text-white text-lg mb-1">
                    {option.title}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {option.description}
                  </p>
                </div>
              </div>

              {/* Toggle Switch */}
              <button
                onClick={() => toggleNotification(option.id)}
                disabled={isRequestingPermission}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
                  notifications[option.id] ? 'bg-blue-500' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-300 ${
                    notifications[option.id] ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>

        {/* Permission Status */}
        {notificationPermission !== 'granted' && (
          <div className="w-full max-w-md mb-4 px-2">
            <div className="bg-blue-900/20 border border-blue-700/50 rounded-xl p-3">
              <div className="flex items-start space-x-3">
                <div className="text-blue-400 mt-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xs font-medium text-blue-300">Notification Permission</h3>
                  <p className="text-xs text-blue-200 mt-1">
                    {notificationPermission === 'default' 
                      ? 'Enable notifications to receive price alerts and updates.'
                      : 'Please allow notifications in your browser settings to receive alerts.'
                    }
                  </p>
                  {notificationPermission === 'default' && (
                    <button
                      onClick={requestNotificationPermission}
                      disabled={isRequestingPermission}
                      className="mt-2 px-2 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      {isRequestingPermission ? 'Requesting...' : 'Enable Notifications'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Next Button */}
        <div className="w-full max-w-md px-2">
          <button
            onClick={handleNext}
            className="w-full py-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
} 